import _ from 'lodash';
import { fontUtils } from '../utils';

import type {
  DocumentServicesObject,
  FontId,
} from '@wix/document-services-types';
import type { FontPickerOnStyleChangePayload } from '@wix/editor-platform-sdk-types';
import type { TPAMessageCallback } from '../types/tpaMessage';
import type { TpaStyleService } from './tpaStyleService';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';

const {
  overrideCustomPresetParams,
  overrideParamsByDisplayName,
  parseFontString,
} = fontUtils;

const styleProperties = new Set(['bold', 'italic', 'underline']);

export const createFontPickerStyleService = (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
) => {
  const isShowWeightPickerForTPAsTextStylePanelEnabled = experimentsAPI.enabled(
    'se_showWeightPickerForTPAsTextStylePanel',
  );

  const getThemeProps = (presetKey: string, displayName?: string) => {
    const textPresets = documentServicesAPI.tpa.style.getNameToFontsKeyMap();
    const preset = textPresets[presetKey];
    const fontString = documentServicesAPI.theme.fonts.get(presetKey as FontId);
    const { size, family, bold, italic, weight } = parseFontString(fontString);

    return {
      family: family.toLowerCase(),
      editorKey: presetKey,
      displayName,
      preset,
      size,
      style: {
        bold,
        italic,
        underline: false,
      },
      ...(isShowWeightPickerForTPAsTextStylePanelEnabled && {
        weight,
      }),
    };
  };

  const getValueFromFontPicker = (
    compStyle: any,
    styles: FontPickerOnStyleChangePayload,
  ) => {
    const [key, value, displayName] = styles;

    let fontParam = {
      style: {
        italic: false,
        bold: false,
        underline: false,
      },
      family: 'arial',
      ...(isShowWeightPickerForTPAsTextStylePanelEnabled && {
        weight: '400',
      }),
    } as any;

    if (compStyle) {
      fontParam = compStyle;
      delete fontParam.displayName;
      delete fontParam.value;
    }

    if (key === 'theme') {
      Object.assign(fontParam, getThemeProps(value.toString(), displayName));
    } else {
      overrideCustomPresetParams(fontParam, key, value, experimentsAPI);
    }

    fontParam.fontStyleParam = true;
    return fontParam;
  };

  const onStyleChange = (
    compId: string,
    compStyle: any,
    wixParam: string,
    styles: FontPickerOnStyleChangePayload,
    callback: TPAMessageCallback,
    setStyleParamsFn: TpaStyleService['setStyleParam'],
  ) => {
    const styleParams = {
      key: wixParam,
      param: {
        value: getValueFromFontPicker(compStyle, styles),
      },
      type: 'font',
    };

    const compRef = documentServicesAPI.components.get.byId(compId);

    if (!compRef) {
      throw new Error(`No compRef found for compId: ${compId}`);
    }

    const styleChangeCallback = () => {
      const updatedStyle = documentServicesAPI.tpa.style.get(compId);
      callback({
        fontParam: updatedStyle.style.fonts[wixParam],
      });
    };

    setStyleParamsFn(compRef, styleParams, styleChangeCallback, false);
  };

  const overrideParamsByKey = (
    styleParams: Record<string, any>,
    stylesToOverride: FontPickerOnStyleChangePayload,
  ) => {
    const [key, value, displayName] = stylesToOverride;

    if (key === 'theme') {
      Object.assign(styleParams, getThemeProps(value.toString(), displayName));
    } else if (styleProperties.has(key)) {
      styleParams.style = { ...(styleParams.style || {}), [key]: value };
    } else {
      styleParams[key] = value;
    }
  };

  const onStyleChangeWithoutWixParam = (
    initialStyleParams: Record<string, any>,
    callback: TPAMessageCallback,
    newStyles: FontPickerOnStyleChangePayload,
  ) => {
    const [_key, _value, displayName] = newStyles;

    const styleParams = _.isPlainObject(initialStyleParams)
      ? initialStyleParams
      : {};

    overrideParamsByKey(styleParams, newStyles);
    overrideParamsByDisplayName(styleParams, displayName);

    callback({ fontParam: styleParams });
  };

  return { onStyleChange, onStyleChangeWithoutWixParam };
};
